import { useMutation, UseMutationOptions } from "@tanstack/react-query";

import { TApiServiceError } from "../apiService";
import { TAsyncTuple } from "../../utils/async";
import { authService } from "./auth.service";
import {
  TResetPasswordRequestPayload,
  TResetPasswordResponsePayload,
  TForgotPasswordRequestPayload,
  TForgotPasswordResponsePayload,
  TOnboardUserRequestPayload,
  TOnboardUserResponsePayload,
  TUpdatePasswordRequestPayload,
  TUpdatePasswordResponsePayload,
  TInviteVerificationResponse,
  TRenewPasswordRequestPayload,
  TRenewPasswordResponsePayload,
} from "./auth.types";
import { buildQueryParams } from "@/utils/commonUtils";

export type TUseLogoutMutationOptions = UseMutationOptions<
  void,
  TApiServiceError,
  string
>;

export const useLogoutMutation = (options?: TUseLogoutMutationOptions) => {
  return useMutation<void, TApiServiceError, string>({
    mutationFn: (refresh_token: string) => authService.logout(refresh_token),
    ...options,
  });
};

export const useResetPasswordMutation = (
  options?: UseMutationOptions<
    TResetPasswordResponsePayload,
    TApiServiceError,
    TResetPasswordRequestPayload
  >,
) => {
  return useMutation<
    TResetPasswordResponsePayload,
    TApiServiceError,
    TResetPasswordRequestPayload
  >({
    mutationFn: async (payload) => {
      const { data, error } = await authService.resetPassword(payload);
      if (error || !data) throw error;
      return data;
    },
    ...options,
  });
};

export const useForgotPasswordMutation = (
  options?: UseMutationOptions<
    TForgotPasswordResponsePayload,
    TApiServiceError,
    TForgotPasswordRequestPayload
  >,
) => {
  return useMutation<
    TForgotPasswordResponsePayload,
    TApiServiceError,
    TForgotPasswordRequestPayload
  >({
    mutationFn: async (payload) => {
      const { data, error } = await authService.forgotPassword(payload);
      if (error || !data) throw error;
      return data;
    },
    ...options,
  });
};

export const useOnboardUserMutation = (
  options?: UseMutationOptions<
    TOnboardUserResponsePayload,
    TApiServiceError,
    TOnboardUserRequestPayload
  > & { tenantId?: string | undefined },
) => {
  return useMutation<
    TOnboardUserResponsePayload,
    TApiServiceError,
    TOnboardUserRequestPayload
  >({
    mutationFn: async (payload) => {
      const { data, error } = await authService.onboardUser(payload, {
        tenantId: options?.tenantId,
      });
      if (error || !data) throw error;
      return data;
    },
    ...options,
  });
};

export const useUpdatePasswordMutation = (
  options?: UseMutationOptions<
    TUpdatePasswordResponsePayload,
    TApiServiceError,
    {
      payload: TUpdatePasswordRequestPayload;
      queryParams?: Record<string, any>;
    }
  >,
) => {
  return useMutation<
    TUpdatePasswordResponsePayload,
    TApiServiceError,
    {
      payload: TUpdatePasswordRequestPayload;
      queryParams?: Record<string, any>;
    }
  >({
    mutationFn: async ({ payload, queryParams }) => {
      const query = queryParams ? buildQueryParams(queryParams) : "";
      const { data, error } = (await authService.updatePassword(
        payload,
        query,
      )) as TAsyncTuple<TApiServiceError, TUpdatePasswordResponsePayload>;
      if (error || !data) throw error;
      return data;
    },
    ...options,
  });
};

export const useInviteVerificationMutation = (
  options?: UseMutationOptions<
    TInviteVerificationResponse,
    TApiServiceError,
    string
  > & { tenantId?: string | undefined },
) => {
  return useMutation<TInviteVerificationResponse, TApiServiceError, string>({
    mutationFn: async (verificationKey) => {
      const { data, error } = await authService.inviteVerification(
        verificationKey,
        { tenantId: options?.tenantId },
      );
      if (error || !data) throw error;
      return data;
    },
    ...options,
  });
};

export const useRenewPasswordMutation = (
  options?: UseMutationOptions<
    TRenewPasswordResponsePayload,
    TApiServiceError,
    TRenewPasswordRequestPayload
  >,
) => {
  return useMutation<
    TRenewPasswordResponsePayload,
    TApiServiceError,
    TRenewPasswordRequestPayload
  >({
    mutationFn: async (payload) => {
      const { data, error } = await authService.renewPassword(payload);
      if (error || !data) throw error;
      return data;
    },
    ...options,
  });
};
